import { useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { EditBlocksForm, TemplateForm } from './EditBlocksForm';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import { useAddTemplatesToBlocks } from '../../hooks/api/use-blocks.hook';
import { BlockApiModel } from '../../../../types/models/block.model';

type Props = {
  isOpen: boolean;
  blocks?: BlockApiModel[];
  isSelectAll?: boolean;
  onClose: () => void;
};

export const EditBlocks = ({ isOpen, blocks, isSelectAll, onClose }: Props) => {
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});
  const [addTemplatesToBlocks, { error: addTemplatesError }] = useAddTemplatesToBlocks();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditBlocksForm>({
    resolver: classValidatorResolver(EditBlocksForm),
  });

  useEffect(() => {
    if (isOpen) {
      reset({ templates: [] });
    }
  }, [isOpen]);

  const onSubmit: SubmitHandler<EditBlocksForm> = async ({ templates }) => {
    if ((blocks ?? []).length === 0 && !isSelectAll) return;

    await addTemplatesToBlocks({
      templateIds: templates.map(({ id }) => id),
      ...(isSelectAll
        ? { isSelectAll }
        : {
            blockIds: blocks ? blocks.map(({ id }) => id) : undefined,
          }),
    });

    onClose();
  };

  const renderContent = () => (
    <Controller
      control={control}
      name="templates"
      render={({ field }) => (
        <Autocomplete
          {...register('templates')}
          loading={loadingTemplates}
          sx={{ mt: '1rem' }}
          multiple
          onChange={(_event, selectedOptions) => field.onChange([...selectedOptions])}
          onBlur={field.onBlur}
          value={field.value}
          getOptionLabel={(template) => (template as TemplateForm)?.name ?? ''}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          options={templatesResponse?.pages.flatMap(({ result }) => result) ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Template(s)"
              error={Boolean(errors.templates)}
              helperText={errors.templates?.message}
            />
          )}
        />
      )}
    />
  );

  return (
    <Dialog open={isOpen}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
        <DialogTitle>Edit Blocks</DialogTitle>
        <DialogContent>
          {renderContent()}
          {addTemplatesError && (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {addTemplatesError?.response?.data?.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={onClose} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
